import { useState, useRef, useCallback } from 'react'
import Editor from '@monaco-editor/react'
import * as LucideIcons from 'lucide-react'
import './editors.css'

/**
 * SQL Script Editor — Monaco editor with file upload support.
 * Used by both DDL Source and DML Source nodes.
 *
 * Accepts single .sql files or ZIP archives containing .sql files.
 * Displays a code editor with dialect-aware syntax highlighting.
 */

// Map our dialect IDs to Monaco language IDs
const DIALECT_TO_LANG = {
  db2: 'sql',
  sqlserver: 'sql',
  postgresql: 'pgsql',
  oracle: 'sql',
  mysql: 'mysql',
}

function SqlScriptEditor({ value = '', onChange, dialect = 'db2', disabled = false, height = '300px', placeholder = '' }) {
  const [files, setFiles] = useState([])
  const [activeTab, setActiveTab] = useState('editor') // 'editor' | 'files'
  const fileInputRef = useRef(null)
  const editorRef = useRef(null)

  const monacoLang = DIALECT_TO_LANG[dialect] || 'sql'

  // Handle editor mount
  const handleEditorMount = useCallback((editor) => {
    editorRef.current = editor
  }, [])

  // Handle file selection (single .sql or .zip)
  const handleFileSelect = useCallback(async (e) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length === 0) return

    const newFiles = []
    let combinedSql = value || ''

    for (const file of selected) {
      if (file.name.endsWith('.zip')) {
        // Handle ZIP files
        try {
          const arrayBuffer = await file.arrayBuffer()
          const entries = await readZipEntries(arrayBuffer)
          for (const entry of entries) {
            if (entry.name.endsWith('.sql')) {
              newFiles.push({ name: entry.name, content: entry.content, fromZip: file.name })
              combinedSql += `\n-- === ${entry.name} ===\n${entry.content}\n`
            }
          }
        } catch (err) {
          console.error('Failed to read ZIP:', err)
          newFiles.push({ name: file.name, content: `-- Error reading ZIP: ${err.message}`, error: true })
        }
      } else if (file.name.endsWith('.sql') || file.name.endsWith('.ddl') || file.name.endsWith('.dml')) {
        // Handle individual SQL files
        const content = await file.text()
        newFiles.push({ name: file.name, content })
        combinedSql += `\n-- === ${file.name} ===\n${content}\n`
      }
    }

    setFiles(prev => [...prev, ...newFiles])
    onChange(combinedSql.trim())

    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [value, onChange])

  // Read ZIP entries using the browser's built-in DecompressionStream (or fallback)
  const readZipEntries = async (arrayBuffer) => {
    // Use JSZip-like manual parsing for ZIP files
    // Since we don't have JSZip, we'll do a simple approach:
    // Read the ZIP as a blob and use the File API
    // For now, use a simpler approach — read as text and look for SQL content
    const entries = []

    try {
      // Try using the ZIP structure directly
      const view = new DataView(arrayBuffer)
      let offset = 0

      while (offset < arrayBuffer.byteLength - 4) {
        const sig = view.getUint32(offset, true)

        // Local file header signature = 0x04034b50
        if (sig !== 0x04034b50) break

        const compressionMethod = view.getUint16(offset + 8, true)
        const compressedSize = view.getUint32(offset + 18, true)
        const uncompressedSize = view.getUint32(offset + 22, true)
        const fileNameLength = view.getUint16(offset + 26, true)
        const extraLength = view.getUint16(offset + 28, true)

        const fileNameBytes = new Uint8Array(arrayBuffer, offset + 30, fileNameLength)
        const fileName = new TextDecoder().decode(fileNameBytes)

        const dataOffset = offset + 30 + fileNameLength + extraLength

        if (compressionMethod === 0 && uncompressedSize > 0) {
          // Stored (no compression) — read directly
          const contentBytes = new Uint8Array(arrayBuffer, dataOffset, uncompressedSize)
          const content = new TextDecoder().decode(contentBytes)
          entries.push({ name: fileName, content })
        } else if (compressionMethod === 8 && compressedSize > 0) {
          // Deflate — use DecompressionStream
          try {
            const compressedData = new Uint8Array(arrayBuffer, dataOffset, compressedSize)
            const ds = new DecompressionStream('deflate-raw')
            const writer = ds.writable.getWriter()
            writer.write(compressedData)
            writer.close()
            const reader = ds.readable.getReader()
            const chunks = []
            while (true) {
              const { done, value: chunk } = await reader.read()
              if (done) break
              chunks.push(chunk)
            }
            const totalLength = chunks.reduce((sum, c) => sum + c.length, 0)
            const result = new Uint8Array(totalLength)
            let pos = 0
            for (const chunk of chunks) {
              result.set(chunk, pos)
              pos += chunk.length
            }
            const content = new TextDecoder().decode(result)
            entries.push({ name: fileName, content })
          } catch {
            entries.push({ name: fileName, content: `-- Could not decompress ${fileName}` })
          }
        }

        offset = dataOffset + compressedSize
      }
    } catch (err) {
      console.error('ZIP parse error:', err)
    }

    return entries
  }

  // Remove a loaded file
  const handleRemoveFile = useCallback((index) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index)
      // Rebuild combined SQL from remaining files
      const combined = updated.map(f => `-- === ${f.name} ===\n${f.content}`).join('\n\n')
      onChange(combined.trim())
      return updated
    })
  }, [onChange])

  // Clear all
  const handleClearAll = useCallback(() => {
    setFiles([])
    onChange('')
  }, [onChange])

  // Count statements (rough: split on semicolons)
  const statementCount = (value || '').split(/;\s*$/m).filter(s => s.trim() && !s.trim().startsWith('--')).length

  return (
    <div className="sql-script-editor">
      {/* Tabs */}
      <div className="sql-editor-tabs">
        <button
          className={`sql-editor-tab ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          <LucideIcons.Code size={12} />
          Editor
          {statementCount > 0 && (
            <span className="sql-editor-count">{statementCount}</span>
          )}
        </button>
        <button
          className={`sql-editor-tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <LucideIcons.Files size={12} />
          Files
          {files.length > 0 && (
            <span className="sql-editor-count">{files.length}</span>
          )}
        </button>
        <div className="sql-editor-tab-spacer" />
        <button
          className="sql-editor-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          title="Upload .sql file or .zip"
        >
          <LucideIcons.Upload size={12} />
          Upload
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".sql,.ddl,.dml,.zip"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Editor tab */}
      {activeTab === 'editor' && (
        <div className="sql-editor-monaco" style={{ height }}>
          <Editor
            height="100%"
            language={monacoLang}
            value={value || ''}
            onChange={(val) => onChange(val || '')}
            theme="vs-dark"
            options={{
              fontSize: 13,
              fontFamily: "'SF Mono', 'Fira Code', 'JetBrains Mono', Consolas, monospace",
              minimap: { enabled: false },
              lineNumbers: 'on',
              renderLineHighlight: 'line',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              readOnly: disabled,
              wordWrap: 'on',
              tabSize: 2,
              padding: { top: 8 },
              scrollbar: {
                verticalScrollbarSize: 6,
                horizontalScrollbarSize: 6,
              },
            }}
          />
          {!value && placeholder && (
            <div className="sql-editor-placeholder">{placeholder}</div>
          )}
        </div>
      )}

      {/* Files tab */}
      {activeTab === 'files' && (
        <div className="sql-editor-files" style={{ height }}>
          {files.length === 0 ? (
            <div className="sql-editor-files-empty">
              <LucideIcons.FileUp size={24} />
              <p>No files loaded</p>
              <p className="sql-editor-files-hint">
                Upload .sql files or a .zip archive
              </p>
              <button
                className="sql-editor-upload-btn-large"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
              >
                <LucideIcons.Upload size={14} />
                Choose Files
              </button>
            </div>
          ) : (
            <>
              <div className="sql-editor-files-list">
                {files.map((file, i) => (
                  <div key={i} className={`sql-editor-file-item ${file.error ? 'error' : ''}`}>
                    <LucideIcons.FileText size={14} />
                    <div className="sql-editor-file-info">
                      <span className="sql-editor-file-name">{file.name}</span>
                      {file.fromZip && (
                        <span className="sql-editor-file-source">from {file.fromZip}</span>
                      )}
                    </div>
                    <span className="sql-editor-file-size">
                      {file.content.length > 1024
                        ? `${(file.content.length / 1024).toFixed(1)} KB`
                        : `${file.content.length} B`}
                    </span>
                    <button
                      className="sql-editor-file-remove"
                      onClick={() => handleRemoveFile(i)}
                      disabled={disabled}
                    >
                      <LucideIcons.X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="sql-editor-files-footer">
                <span>{files.length} file(s), ~{statementCount} statements</span>
                <button
                  className="sql-editor-clear-btn"
                  onClick={handleClearAll}
                  disabled={disabled}
                >
                  Clear All
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SqlScriptEditor
